import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API, useTemplates } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Users, Shield, User, Trash2, Edit2, Search, Mail, Calendar, UserPlus, Copy, Check, Palette, Building, UserCog, Coins, FileText, Layers, Clock, BookOpen, LayoutGrid, Table as TableIcon, Download, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { exportCsv } from "@/utils/csv";

export default function UserManagementPage() {
  const { user: currentUser, refreshUser } = useAuth();
  const { templates } = useTemplates();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [editRole, setEditRole] = useState("");
  const [editTemplateAccess, setEditTemplateAccess] = useState([]);
  const [editOrganization, setEditOrganization] = useState("");
  // Per-user quota fields
  const [editAccountType, setEditAccountType] = useState("paid");
  const [editMaxDeckSlides, setEditMaxDeckSlides] = useState(25);
  const [editMaxContentUploads, setEditMaxContentUploads] = useState(10);
  // 0 = inherit plan default (trial=2, basic=3, pro=5, enterprise=25).
  // Positive int = per-user override.
  const [editMaxConcurrentRuns, setEditMaxConcurrentRuns] = useState(0);
  const [editAllowedFileTypes, setEditAllowedFileTypes] = useState([]);
  // Credits
  const [creditTopUp, setCreditTopUp] = useState("");
  const [creditTopUpReason, setCreditTopUpReason] = useState("");
  const [creditDirection, setCreditDirection] = useState("add"); // "add" | "deduct"
  const [toppingUp, setToppingUp] = useState(false);
  // Credits ledger viewer
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  // Impersonation
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Invite user state
  const [showInvite, setShowInvite] = useState(false);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("user");
  const [inviteTemplateAccess, setInviteTemplateAccess] = useState([]);
  const [inviteOrganization, setInviteOrganization] = useState("");
  const [inviteAccountType, setInviteAccountType] = useState("trial");
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [copiedPassword, setCopiedPassword] = useState(false);
  // Default to the comprehensive table view — admins asked for at-a-glance
  // metrics (purchased credits, queued jobs, etc.) which only fit in tabular
  // form. Cards remain available via the toggle for admins who prefer them.
  const [viewMode, setViewMode] = useState("table"); // "table" | "cards"

  // Onboarding admin controls (2026-05-26):
  //   • `selectedUserIds` — multi-select state for bulk retrigger. Lives at
  //     the page level so we can render the bulk-action bar above the table.
  //   • `onboardingConfigOpen` — toggles the sample-content editor dialog.
  //   • `onboardingConfig` — local cache of the singleton; lazy-loaded the
  //     first time the admin opens the dialog.
  const [selectedUserIds, setSelectedUserIds] = useState(() => new Set());
  const [bulkRetriggering, setBulkRetriggering] = useState(false);
  const [retriggeringUserId, setRetriggeringUserId] = useState(null);
  const [onboardingConfigOpen, setOnboardingConfigOpen] = useState(false);
  const [onboardingConfig, setOnboardingConfig] = useState(null);
  const [onboardingConfigLoading, setOnboardingConfigLoading] = useState(false);
  const [onboardingConfigSaving, setOnboardingConfigSaving] = useState(false);
  const [ocSampleTitle, setOcSampleTitle] = useState("");
  const [ocSampleContent, setOcSampleContent] = useState("");

  const toggleUserSelected = (userId) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const clearSelection = () => setSelectedUserIds(new Set());

  const handleRetriggerOnboarding = async (u) => {
    if (!u) return;
    if (!window.confirm(`Re-trigger the coachmark onboarding tour for ${u.email}?\n\nThe tour will fire next time they open PPT Studio.`)) {
      return;
    }
    setRetriggeringUserId(u.user_id);
    try {
      await axios.post(
        `${API}/admin/users/${u.user_id}/retrigger-onboarding`,
        {},
        { withCredentials: true },
      );
      toast.success(`Onboarding re-triggered for ${u.email}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't retrigger onboarding");
    } finally {
      setRetriggeringUserId(null);
    }
  };

  const handleBulkRetrigger = async () => {
    const ids = Array.from(selectedUserIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Re-trigger the coachmark onboarding tour for ${ids.length} user${ids.length === 1 ? "" : "s"}?\n\nEveryone selected will see the tour next time they open PPT Studio.`)) {
      return;
    }
    setBulkRetriggering(true);
    try {
      const res = await axios.post(
        `${API}/admin/users/retrigger-onboarding-bulk`,
        { user_ids: ids },
        { withCredentials: true },
      );
      const modified = res.data?.modified ?? 0;
      toast.success(`Onboarding re-triggered for ${modified} user${modified === 1 ? "" : "s"}`);
      clearSelection();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk retrigger failed");
    } finally {
      setBulkRetriggering(false);
    }
  };

  const openOnboardingConfig = async () => {
    setOnboardingConfigOpen(true);
    // Lazy-load: only hit the endpoint when the admin actually opens the
    // dialog. Subsequent opens reuse the cached doc — close-and-save flushes.
    if (onboardingConfig) return;
    setOnboardingConfigLoading(true);
    try {
      const res = await axios.get(`${API}/onboarding-config`, { withCredentials: true });
      setOnboardingConfig(res.data);
      setOcSampleTitle(res.data?.sample_title || "");
      setOcSampleContent(res.data?.sample_content || "");
    } catch (err) {
      toast.error("Couldn't load onboarding config");
    } finally {
      setOnboardingConfigLoading(false);
    }
  };

  const handleSaveOnboardingConfig = async () => {
    if (!ocSampleTitle.trim() || !ocSampleContent.trim()) {
      toast.error("Both fields are required");
      return;
    }
    if (ocSampleContent.trim().length < 100) {
      toast.error("Sample content must be at least 100 chars — short content trips the input-strength guard");
      return;
    }
    setOnboardingConfigSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/onboarding-config`,
        { sample_title: ocSampleTitle, sample_content: ocSampleContent },
        { withCredentials: true },
      );
      setOnboardingConfig({
        sample_title: res.data.sample_title,
        sample_content: res.data.sample_content,
      });
      toast.success("Onboarding sample content saved");
      setOnboardingConfigOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setOnboardingConfigSaving(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(res.data);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const ALL_FILE_TYPES = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".png", ".jpg", ".jpeg", ".webp"];
  const TRIAL_DEFAULTS = { max_deck_slides: 7, max_content_uploads: 2, allowed_file_types: [".pdf"] };
  const PAID_DEFAULTS = { max_deck_slides: 25, max_content_uploads: 10, allowed_file_types: ALL_FILE_TYPES };

  const handleTopUp = async () => {
    if (!editingUser) return;
    const amt = parseInt(creditTopUp);
    if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
    const signed = creditDirection === "deduct" ? -amt : amt;
    // Confirmation prompt — credit changes are visible to the user as a
    // notification, so prevent fat-finger mistakes.
    const verb = creditDirection === "deduct" ? "deduct" : "add";
    const confirmed = window.confirm(
      `Are you sure you want to ${verb} ${amt} credit${amt !== 1 ? 's' : ''} ${verb === 'deduct' ? 'from' : 'to'} ${editingUser.email}?\n\n` +
      `${creditTopUpReason ? `Reason: ${creditTopUpReason}\n\n` : ''}` +
      `The user will receive a notification about this change.`
    );
    if (!confirmed) return;
    setToppingUp(true);
    try {
      const res = await axios.post(`${API}/admin/users/${editingUser.user_id}/credits`, {
        amount: signed,
        reason: creditTopUpReason || (creditDirection === "deduct" ? "manual_debit" : "manual_topup"),
      }, { withCredentials: true });
      toast.success(
        creditDirection === "deduct"
          ? `Deducted ${Math.abs(res.data.delta)} credits — new balance: ${res.data.credits_balance}`
          : `Added ${res.data.delta} credits — new balance: ${res.data.credits_balance}`
      );
      setCreditTopUp(""); setCreditTopUpReason("");
      setEditingUser({ ...editingUser, credits_balance: res.data.credits_balance });
      // If the ledger is open, refresh it so the new entry is visible immediately
      if (ledgerOpen) loadLedger(editingUser.user_id);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Credit adjustment failed");
    } finally {
      setToppingUp(false);
    }
  };

  const loadLedger = async (userId) => {
    setLedgerLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/credits/ledger`, { withCredentials: true });
      setLedgerData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load ledger");
    } finally {
      setLedgerLoading(false);
    }
  };

  const toggleLedger = async () => {
    if (!editingUser) return;
    const next = !ledgerOpen;
    setLedgerOpen(next);
    if (next && !ledgerData) await loadLedger(editingUser.user_id);
  };

  const handleImpersonate = async (u) => {
    if (!u || u.role === "admin") return;
    if (!window.confirm(`Login as ${u.name || u.email}?\n\nYou'll see exactly what they see. A persistent banner will let you exit anytime, and every impersonation is logged for audit.`)) {
      return;
    }
    setImpersonatingId(u.user_id);
    try {
      await axios.post(`${API}/admin/users/${u.user_id}/impersonate`, {}, { withCredentials: true });
      // Hard reload so AuthContext re-fetches /auth/me with the new session
      // and the impersonation banner mounts immediately.
      window.location.href = "/generator";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start impersonation");
      setImpersonatingId(null);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${editingUser.user_id}`, { 
        role: editRole,
        template_access: editTemplateAccess,
        organization: editOrganization,
        account_type: editAccountType,
        max_deck_slides: Number(editMaxDeckSlides) || 25,
        max_content_uploads: Number(editMaxContentUploads) || 10,
        allowed_file_types: editAllowedFileTypes,
        // 0 = use the plan default; positive int = per-user override.
        max_concurrent_runs_override: Number(editMaxConcurrentRuns) || 0,
      }, { withCredentials: true });
      toast.success("User updated");
      // If the admin edited their own account, refresh the AuthContext so
      // the new role / plan / template_access takes effect immediately in
      // the current session (otherwise they'd need to reload to see it).
      if (editingUser.user_id === currentUser?.user_id) {
        await refreshUser();
      }
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/users/${deleteUser.user_id}`, { withCredentials: true });
      toast.success("User deleted");
      setDeleteUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    } finally {
      setSaving(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteName || !inviteEmail) {
      toast.error("Please fill in name and email");
      return;
    }
    setInviting(true);
    try {
      const res = await axios.post(`${API}/admin/users/invite`, {
        name: inviteName,
        email: inviteEmail,
        role: inviteRole,
        template_access: inviteTemplateAccess,
        organization: inviteOrganization,
        account_type: inviteAccountType,
        // Apply default quotas matching the chosen account_type; admin can edit afterwards
        ...(inviteAccountType === "trial" ? TRIAL_DEFAULTS : PAID_DEFAULTS),
      }, { withCredentials: true });
      setInviteResult(res.data);
      toast.success("User invited successfully!");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to invite user");
    } finally {
      setInviting(false);
    }
  };

  const handleCopyPassword = () => {
    if (inviteResult?.temp_password) {
      navigator.clipboard.writeText(inviteResult.temp_password);
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    }
  };

  const closeInviteDialog = () => {
    setShowInvite(false);
    setInviteName("");
    setInviteEmail("");
    setInviteRole("user");
    setInviteTemplateAccess([]);
    setInviteOrganization("");
    setInviteAccountType("trial");
    setInviteResult(null);
    setCopiedPassword(false);
  };

  const openEditDialog = (u) => {
    setEditingUser(u);
    // Reset ledger state for the new user — avoids showing stale data when
    // admins jump between user rows.
    setLedgerOpen(false);
    setLedgerData(null);
    setCreditDirection("add");
    setCreditTopUp("");
    setCreditTopUpReason("");
    setEditRole(u.role || "user");
    setEditTemplateAccess(u.template_access || []);
    setEditOrganization(u.organization || "");
    // Map legacy "paid" → "basic" so the Select control finds a matching
    // option for users created before the trial/basic/pro/enterprise rename.
    let at = (u.account_type || "trial").toLowerCase();
    if (at === "paid") at = "basic";
    setEditAccountType(at);
    const defaults = at === "trial" ? TRIAL_DEFAULTS : PAID_DEFAULTS;
    setEditMaxDeckSlides(u.max_deck_slides || defaults.max_deck_slides);
    setEditMaxContentUploads(u.max_content_uploads || defaults.max_content_uploads);
    setEditMaxConcurrentRuns(u.max_concurrent_runs_override || 0);
    setEditAllowedFileTypes(
      (u.allowed_file_types && u.allowed_file_types.length > 0)
        ? u.allowed_file_types
        : defaults.allowed_file_types
    );
  };

  const toggleFileType = (ext) => {
    if (editAllowedFileTypes.includes(ext)) {
      setEditAllowedFileTypes(editAllowedFileTypes.filter(e => e !== ext));
    } else {
      setEditAllowedFileTypes([...editAllowedFileTypes, ext]);
    }
  };

  const toggleTemplateAccess = (templateId, list, setList) => {
    if (list.includes(templateId)) {
      setList(list.filter(id => id !== templateId));
    } else {
      setList([...list, templateId]);
    }
  };

  const selectAllTemplates = (setList) => {
    setList(templates.map(t => t.id));
  };

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const fmtDate = (d) => { try { return format(new Date(d), "MMM d, yyyy"); } catch { return d; } };

  // Compact relative time for "Last login" (e.g. "3h ago", "yesterday").
  // Falls back to the absolute date for anything older than ~30 days.
  const fmtRelative = (d) => {
    if (!d) return "";
    try {
      const then = new Date(d);
      const diffMs = Date.now() - then.getTime();
      const mins = Math.floor(diffMs / 60_000);
      if (mins < 1)          return "just now";
      if (mins < 60)         return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24)          return `${hrs}h ago`;
      const days = Math.floor(hrs / 24);
      if (days === 1)        return "yesterday";
      if (days < 30)         return `${days}d ago`;
      return format(then, "MMM d, yyyy");
    } catch { return d; }
  };

  // Build a CSV snapshot of whatever the admin currently sees (post-search
  // filter). Keeps every column from the new UserTable + a few we don't
  // surface visually (id, role, monthly cap) so the export is useful for
  // offline analysis / quarterly reviews.
  const handleExportCsv = () => {
    const headers = [
      "User ID", "Name", "Email", "Role", "Plan", "Organization",
      "Active credits", "Purchased credits",
      "Reports total", "Slides", "Decks", "Deck slides", "Queued",
      "Templates", "Joined", "Last login", "Login count",
    ];
    const rows = [headers, ...filteredUsers.map(u => {
      const s = u.stats || {};
      return [
        u.user_id || "",
        u.name || "",
        u.email || "",
        u.role || "user",
        u.account_type || "trial",
        u.organization || u.organization_inferred || "",
        u.credits_balance ?? 0,
        u.credits_purchased_total ?? 0,
        s.reports_total ?? 0,
        s.singles_complete ?? 0,
        s.decks_complete ?? 0,
        s.deck_slides_complete ?? 0,
        s.queued_total ?? 0,
        getTemplateNames(u.template_access),
        u.created_at ? fmtDate(u.created_at) : "",
        u.last_login_at ? fmtDate(u.last_login_at) : "",
        u.login_count ?? 0,
      ];
    })];
    const stamp = new Date().toISOString().split("T")[0];
    exportCsv(`wokelo-users-${stamp}.csv`, rows);
    toast.success(`Exported ${filteredUsers.length} users`);
  };

  const getTemplateNames = (templateIds) => {
    if (!templateIds || templateIds.length === 0) return "All templates";
    if (templateIds.length === templates.length) return "All templates";
    return templates
      .filter(t => templateIds.includes(t.id))
      .map(t => t.name)
      .join(", ") || "None";
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="user-management-page">
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
        <span>Admin</span><span>/</span><span className="text-gray-900">User Management</span>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] flex items-center gap-2">
            <Users className="w-6 h-6" />User Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">{users.length} registered users</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View-mode toggle — table view shows per-user metrics that don't
              fit on the card layout (purchased credits, deck slides, etc.) */}
          <div className="flex items-center rounded-lg border border-gray-200 p-0.5 bg-white">
            <button
              onClick={() => setViewMode("table")}
              className={`p-1.5 rounded transition-colors ${viewMode === "table" ? "bg-gray-900 text-white" : "text-gray-500 hover:text-gray-900"}`}
              title="Table view"
              data-testid="view-mode-table"
            >
              <TableIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("cards")}
              className={`p-1.5 rounded transition-colors ${viewMode === "cards" ? "bg-gray-900 text-white" : "text-gray-500 hover:text-gray-900"}`}
              title="Card view"
              data-testid="view-mode-cards"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
          <Button
            onClick={handleExportCsv}
            variant="outline"
            disabled={!filteredUsers.length}
            data-testid="users-export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />Export CSV
          </Button>
          <Button
            onClick={openOnboardingConfig}
            variant="outline"
            data-testid="onboarding-settings-btn"
            title="Edit the sample title + content shown in the onboarding tour"
          >
            <Sparkles className="w-4 h-4 mr-2" />Onboarding
          </Button>
          <Button onClick={() => setShowInvite(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="invite-user-btn">
            <UserPlus className="w-4 h-4 mr-2" />Invite User
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search users by name or email..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="user-search"
        />
      </div>

      {/* Bulk action bar — surfaces only when 1+ users are selected. */}
      {selectedUserIds.size > 0 && (
        <div
          className="flex items-center justify-between gap-3 mb-4 px-4 py-2.5 rounded-lg bg-teal-50 border border-teal-200"
          data-testid="bulk-action-bar"
        >
          <div className="flex items-center gap-2 text-sm text-teal-900">
            <Check className="w-4 h-4 text-teal-600" />
            <span className="font-medium" data-testid="bulk-selected-count">
              {selectedUserIds.size} selected
            </span>
            <button
              onClick={clearSelection}
              className="text-xs text-teal-700 underline hover:text-teal-900 ml-1"
              data-testid="bulk-clear-selection"
            >
              Clear
            </button>
          </div>
          <Button
            size="sm"
            onClick={handleBulkRetrigger}
            disabled={bulkRetriggering}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="bulk-retrigger-onboarding"
          >
            {bulkRetriggering ? (
              <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5 mr-2" />
            )}
            Retrigger onboarding ({selectedUserIds.size})
          </Button>
        </div>
      )}

      {/* User List */}
      {viewMode === "table" ? (
        <UserTable
          users={filteredUsers}
          currentUser={currentUser}
          templates={templates}
          getTemplateNames={getTemplateNames}
          fmtDate={fmtDate}
          fmtRelative={fmtRelative}
          onImpersonate={handleImpersonate}
          onEdit={openEditDialog}
          onDelete={setDeleteUser}
          onLedger={(u) => navigate(`/admin/users/${u.user_id}/credits`)}
          onLogs={(u) => navigate(`/admin/logs?user_id=${encodeURIComponent(u.user_id)}`, {
            // Pass a snapshot so the deep-link banner renders immediately,
            // even before the logs payload comes back (or if the user has
            // zero logs, the banner still surfaces).
            state: { user: { user_id: u.user_id, name: u.name, email: u.email, organization: u.organization } },
          })}
          onRetriggerOnboarding={handleRetriggerOnboarding}
          retriggeringUserId={retriggeringUserId}
          selectedUserIds={selectedUserIds}
          onToggleSelected={toggleUserSelected}
          impersonatingId={impersonatingId}
        />
      ) : (
      <div className="space-y-3">
        {filteredUsers.map(u => (
          <Card key={u.user_id} className="border-gray-200 shadow-none" data-testid={`user-card-${u.user_id}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {u.picture ? (
                    <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">{u.name || "No name"}</p>
                      {u.role === "admin" && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                          <Shield className="w-2.5 h-2.5" />Admin
                        </span>
                      )}
                      {u.user_id === currentUser?.user_id && (
                        <span className="text-[10px] font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">You</span>
                      )}
                      {u.auth_provider === "invited" && (
                        <span className="text-[10px] font-medium text-teal-600 bg-teal-50 rounded-full px-2 py-0.5">Invited</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</span>
                      {u.organization && (
                        <span className="flex items-center gap-1"><Building className="w-3 h-3" />{u.organization}</span>
                      )}
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Joined {fmtDate(u.created_at)}</span>
                    </div>
                    {u.role !== "admin" && templates.length > 0 && (
                      <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                        <Palette className="w-3 h-3" />
                        <span className="truncate max-w-[250px]">{getTemplateNames(u.template_access)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleImpersonate(u)}
                    disabled={u.user_id === currentUser?.user_id || u.role === "admin" || impersonatingId === u.user_id}
                    title={u.role === "admin" ? "Cannot impersonate another admin" : "Login as this user"}
                    data-testid={`impersonate-${u.user_id}`}
                  >
                    {impersonatingId === u.user_id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <UserCog className="w-3.5 h-3.5" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditDialog(u)}
                    data-testid={`edit-user-${u.user_id}`}
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRetriggerOnboarding(u)}
                    disabled={retriggeringUserId === u.user_id}
                    title="Re-trigger the onboarding coachmark tour"
                    data-testid={`card-retrigger-onboarding-${u.user_id}`}
                  >
                    {retriggeringUserId === u.user_id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Sparkles className="w-3.5 h-3.5" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDeleteUser(u)}
                    disabled={u.user_id === currentUser?.user_id}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-user-${u.user_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredUsers.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            {searchQuery ? "No users found matching your search" : "No users yet"}
          </div>
        )}
      </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1" data-testid="edit-user-body">
            <div>
              <p className="text-sm font-medium text-gray-700">{editingUser?.name}</p>
              <p className="text-xs text-gray-400">{editingUser?.email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Role</label>
              <Select value={editRole} onValueChange={setEditRole}>
                <SelectTrigger data-testid="role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Organization</label>
              <Input
                placeholder="e.g., Wokelo, Acme Corp"
                value={editOrganization}
                onChange={e => setEditOrganization(e.target.value)}
                data-testid="edit-organization-input"
              />
            </div>
            
            {/* Plan / quota config — visible for every user including
                admins, since admin self-edit needs access to the same
                upload limits / subscription / file types controls. */}
            <div className="border-t border-gray-100 pt-4 space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Plan &amp; Limits</p>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Account type</label>
                  <Select value={editAccountType} onValueChange={(v) => {
                    setEditAccountType(v);
                    const def = v === "trial" ? TRIAL_DEFAULTS : PAID_DEFAULTS;
                    setEditMaxDeckSlides(def.max_deck_slides);
                    setEditMaxContentUploads(def.max_content_uploads);
                    setEditAllowedFileTypes(def.allowed_file_types);
                  }}>
                    <SelectTrigger data-testid="edit-account-type"><SelectValue placeholder="Select plan..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="basic">Starter</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Max deck slides</label>
                    <Input type="number" min="1" max="50" value={editMaxDeckSlides}
                      onChange={e => setEditMaxDeckSlides(e.target.value)}
                      data-testid="edit-max-deck-slides" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Max content uploads</label>
                    <Input type="number" min="0" max="50" value={editMaxContentUploads}
                      onChange={e => setEditMaxContentUploads(e.target.value)}
                      data-testid="edit-max-content-uploads" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Concurrent runs override
                    <span className="text-xs text-gray-400 font-normal ml-1">(0 = use plan default)</span>
                  </label>
                  <Input
                    type="number" min="0" max="50"
                    value={editMaxConcurrentRuns}
                    onChange={e => setEditMaxConcurrentRuns(e.target.value)}
                    placeholder="0"
                    data-testid="edit-max-concurrent-runs"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">
                    Plan defaults: Trial 2 · Starter 3 · Pro 5 · Enterprise 25.
                    Set a number here to override for this user only.
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Allowed file types</label>
                  <div className="flex flex-wrap gap-2">
                    {ALL_FILE_TYPES.map(ext => (
                      <label key={ext}
                        className={`px-2 py-1 rounded border text-xs cursor-pointer transition-colors ${editAllowedFileTypes.includes(ext) ? "border-black bg-black text-white" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}>
                        <input type="checkbox" className="sr-only"
                          checked={editAllowedFileTypes.includes(ext)}
                          onChange={() => toggleFileType(ext)} />
                        {ext}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Credits balance + admin top-up */}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Credits</span>
                    <span className="text-lg font-bold text-gray-900" data-testid="edit-credits-balance">
                      {editingUser?.credits_balance ?? 0}
                    </span>
                  </div>

                  {/* Direction toggle — admins can add or deduct in one place */}
                  <div className="inline-flex bg-white border border-gray-200 rounded-md p-0.5 mb-2" data-testid="credits-direction-toggle">
                    <button
                      type="button"
                      onClick={() => setCreditDirection("add")}
                      className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${creditDirection === "add" ? "bg-gray-900 text-white" : "text-gray-600 hover:text-gray-900"}`}
                      data-testid="credits-dir-add"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => setCreditDirection("deduct")}
                      className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${creditDirection === "deduct" ? "bg-gray-900 text-white" : "text-gray-600 hover:text-gray-900"}`}
                      data-testid="credits-dir-deduct"
                    >
                      Deduct
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Input
                      type="number"
                      min="1"
                      placeholder={creditDirection === "deduct" ? "Deduct credits" : "Add credits"}
                      value={creditTopUp}
                      onChange={e => setCreditTopUp(e.target.value)}
                      className="w-32"
                      data-testid="credits-topup-amount"
                    />
                    <Input
                      placeholder="Reason (optional)"
                      value={creditTopUpReason}
                      onChange={e => setCreditTopUpReason(e.target.value)}
                      className="flex-1 min-w-[140px]"
                      data-testid="credits-topup-reason"
                    />
                    <Button
                      onClick={handleTopUp}
                      disabled={toppingUp || !creditTopUp}
                      size="sm"
                      className="flex-shrink-0 bg-black text-white hover:bg-gray-800 hover:text-white disabled:bg-gray-300 disabled:text-gray-500"
                      data-testid="credits-topup-btn"
                    >
                      {toppingUp ? <Loader2 className="w-3 h-3 animate-spin" /> : (creditDirection === "deduct" ? "Deduct" : "Add")}
                    </Button>
                  </div>

                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[11px] text-gray-400">
                      Trial = 50 credits one-time · Starter = 100 / month · Pro = 500 / month · 1 slide = 5 credits
                    </p>
                    <button
                      type="button"
                      onClick={toggleLedger}
                      className="text-[11px] font-semibold text-teal-700 hover:text-teal-900"
                      data-testid="toggle-ledger-btn"
                    >
                      {ledgerOpen ? "Hide ledger" : "View ledger"}
                    </button>
                  </div>

                  {/* Combined ledger viewer */}
                  {ledgerOpen && (
                    <div className="mt-3 rounded-md border border-gray-200 bg-white" data-testid="credits-ledger-panel">
                      {ledgerLoading ? (
                        <div className="flex justify-center py-6">
                          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                        </div>
                      ) : ledgerData?.rows?.length ? (
                        <>
                          <div className="grid grid-cols-3 gap-2 px-3 py-2 border-b border-gray-100 text-[11px] text-gray-500">
                            <div>Granted total: <strong className="text-gray-700">{ledgerData.user.credits_granted_total ?? 0}</strong></div>
                            <div>Spent total: <strong className="text-gray-700">{ledgerData.user.credits_spent_total ?? 0}</strong></div>
                            <div>Current: <strong className="text-gray-900">{ledgerData.user.credits_balance ?? 0}</strong></div>
                          </div>
                          <div className="max-h-64 overflow-y-auto">
                            <table className="w-full text-xs">
                              <thead className="sticky top-0 bg-white z-10">
                                <tr className="text-left text-gray-500 border-b border-gray-100">
                                  <th className="font-semibold px-3 py-2">Date</th>
                                  <th className="font-semibold px-3 py-2">Description</th>
                                  <th className="font-semibold px-3 py-2 text-right">Δ</th>
                                  <th className="font-semibold px-3 py-2 text-right">Balance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ledgerData.rows.map((r, i) => {
                                  // Composite key: ledger entries are immutable
                                  // (append-only) so date+kind+delta is stable
                                  // and unique enough to avoid React reordering bugs.
                                  const k = `${r.date || i}-${r.kind || ""}-${r.delta ?? ""}-${i}`;
                                  return (
                                  <tr key={k} className="border-b border-gray-50 last:border-b-0" data-testid={`ledger-row-${i}`}>
                                    <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">
                                      {r.date ? format(new Date(r.date), "MMM d, HH:mm") : "—"}
                                    </td>
                                    <td className="px-3 py-1.5 text-gray-700">
                                      {r.description}
                                      {r.kind === "payment" && r.status && r.status !== "paid" && (
                                        <span className="ml-2 text-[10px] uppercase font-semibold text-amber-700">{r.status}</span>
                                      )}
                                    </td>
                                    <td className={`px-3 py-1.5 text-right font-mono font-semibold ${r.delta > 0 ? "text-emerald-700" : r.delta < 0 ? "text-red-600" : "text-gray-400"}`}>
                                      {r.delta > 0 ? "+" : ""}{r.delta || "—"}
                                    </td>
                                    <td className="px-3 py-1.5 text-right text-gray-500 font-mono">
                                      {r.balance_after ?? "—"}
                                    </td>
                                  </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      ) : (
                        <p className="text-xs text-gray-400 text-center py-6">No credit history yet.</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

            {/* Template Access — visible for every user (including admins). */}
            {templates.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Template Access</label>
                  <button 
                    onClick={() => selectAllTemplates(setEditTemplateAccess)}
                    className="text-xs text-teal-600 hover:text-teal-700"
                  >
                    Select all
                  </button>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3">
                  {templates.map(t => (
                    <div key={t.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`edit-template-${t.id}`}
                        checked={editTemplateAccess.includes(t.id)}
                        onCheckedChange={() => toggleTemplateAccess(t.id, editTemplateAccess, setEditTemplateAccess)}
                      />
                      <label htmlFor={`edit-template-${t.id}`} className="text-sm text-gray-700 cursor-pointer">
                        {t.name}{t.is_default ? " (Default)" : ""}
                      </label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {editTemplateAccess.length === 0 ? "User will have access to all templates" : `${editTemplateAccess.length} template(s) selected`}
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
            <Button variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
            <Button onClick={handleUpdateUser} disabled={saving} className="bg-black hover:bg-gray-800" data-testid="save-user-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">
            Are you sure you want to delete <strong>{deleteUser?.name}</strong> ({deleteUser?.email})? This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUser(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onboarding Sample Content Editor — singleton config used by the
          'Use sample content' shortcut inside the coachmark tour. Stored
          under `onboarding_config._id = 'singleton'`. */}
      <Dialog open={onboardingConfigOpen} onOpenChange={(open) => !open && setOnboardingConfigOpen(false)}>
        <DialogContent className="max-w-xl" data-testid="onboarding-config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-teal-600" />
              Onboarding Sample Content
            </DialogTitle>
            <DialogDescription>
              The starter title + content that gets auto-filled when a new user clicks <em>Use sample content</em> in the onboarding tour. Content must be at least 100 chars so it passes the input-strength guard.
            </DialogDescription>
          </DialogHeader>
          {onboardingConfigLoading ? (
            <div className="py-10 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                  Sample title
                </label>
                <Input
                  value={ocSampleTitle}
                  onChange={e => setOcSampleTitle(e.target.value)}
                  placeholder="e.g. AI in Enterprise Software — 2026 Market Snapshot"
                  data-testid="onboarding-config-title-input"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  Pre-fills the slide / deck title field during the tour.
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Sample content
                  </label>
                  <span className={`text-[11px] ${ocSampleContent.trim().length < 100 ? "text-red-500" : "text-gray-400"}`}
                        data-testid="onboarding-config-char-count">
                    {ocSampleContent.trim().length} chars
                    {ocSampleContent.trim().length < 100 && " · min 100"}
                  </span>
                </div>
                <textarea
                  value={ocSampleContent}
                  onChange={e => setOcSampleContent(e.target.value)}
                  rows={8}
                  className="flex w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-300 resize-y"
                  placeholder="Paste a high-quality starter paragraph that demonstrates a good content prompt..."
                  data-testid="onboarding-config-content-input"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  Pre-fills the content textarea during the tour.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOnboardingConfigOpen(false)} data-testid="onboarding-config-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleSaveOnboardingConfig}
              disabled={onboardingConfigSaving || onboardingConfigLoading}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="onboarding-config-save"
            >
              {onboardingConfigSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={showInvite} onOpenChange={closeInviteDialog}>
        <DialogContent className="max-w-md max-h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
            <DialogTitle>Invite New User</DialogTitle>
            <DialogDescription>
              Create an account for a new team member. They'll receive a temporary password.
            </DialogDescription>
          </DialogHeader>
          
          {!inviteResult ? (
            <>
              <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Name</label>
                  <Input
                    placeholder="John Doe"
                    value={inviteName}
                    onChange={e => setInviteName(e.target.value)}
                    data-testid="invite-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Email</label>
                  <Input
                    type="email"
                    placeholder="john@company.com"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    data-testid="invite-email-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Role</label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger data-testid="invite-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Organization</label>
                  <Input
                    placeholder="e.g., Wokelo, Acme Corp"
                    value={inviteOrganization}
                    onChange={e => setInviteOrganization(e.target.value)}
                    data-testid="invite-organization-input"
                  />
                </div>

                {inviteRole !== "admin" && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Account type</label>
                    <Select value={inviteAccountType} onValueChange={setInviteAccountType}>
                      <SelectTrigger data-testid="invite-account-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="trial">Trial · 7 slides · 2 PDFs</SelectItem>
                        <SelectItem value="paid">Paid · 25 slides · 10 files (Word/PDF/Excel/PPT/images)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-400 mt-1">
                      Default limits apply; you can fine-tune them after inviting via Edit.
                    </p>
                  </div>
                )}

                {/* Template Access for invite - only for non-admin */}
                {inviteRole !== "admin" && templates.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">Template Access</label>
                      <button 
                        onClick={() => selectAllTemplates(setInviteTemplateAccess)}
                        className="text-xs text-teal-600 hover:text-teal-700"
                      >
                        Select all
                      </button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                      {templates.map(t => (
                        <div key={t.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`invite-template-${t.id}`}
                            checked={inviteTemplateAccess.includes(t.id)}
                            onCheckedChange={() => toggleTemplateAccess(t.id, inviteTemplateAccess, setInviteTemplateAccess)}
                          />
                          <label htmlFor={`invite-template-${t.id}`} className="text-sm text-gray-700 cursor-pointer">
                            {t.name}{t.is_default ? " (Default)" : ""}
                          </label>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {inviteTemplateAccess.length === 0 ? "User will have access to all templates" : `${inviteTemplateAccess.length} template(s) selected`}
                    </p>
                  </div>
                )}
              </div>
              <DialogFooter className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
                <Button variant="outline" onClick={closeInviteDialog}>Cancel</Button>
                <Button onClick={handleInvite} disabled={inviting} className="bg-teal-600 hover:bg-teal-700" data-testid="send-invite-btn">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Invite"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="px-6 py-4 overflow-y-auto flex-1">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm font-medium text-green-800">User invited successfully!</p>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500">Name</p>
                    <p className="text-sm font-medium">{inviteResult.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm font-medium">{inviteResult.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Role</p>
                    <p className="text-sm font-medium capitalize">{inviteResult.role}</p>
                  </div>
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Temporary Password</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono">
                        {inviteResult.temp_password}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCopyPassword}
                        className="shrink-0"
                      >
                        {copiedPassword ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-[10px] text-amber-600 mt-2">
                      Share this password securely with the user. They should change it after first login.
                    </p>
                  </div>
                </div>
              </div>
              <DialogFooter className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
                <Button onClick={closeInviteDialog} className="bg-black hover:bg-gray-800">Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// UserTable — comprehensive admin view: organization, plan, credits
// (purchased + active), templates, generation stats. Edit/Impersonate
// trigger the parent's existing handlers; the credits-ledger button
// opens the Edit dialog where the ledger viewer already lives.
// ─────────────────────────────────────────────────────────────────────
function UserTable({
  users, currentUser, templates, getTemplateNames, fmtDate, fmtRelative,
  onImpersonate, onEdit, onDelete, onLedger, onLogs, impersonatingId,
  onRetriggerOnboarding, retriggeringUserId,
  selectedUserIds, onToggleSelected,
}) {
  const planBadgeClass = (at) => {
    switch (at) {
      case "enterprise": return "bg-purple-50 text-purple-700 border-purple-200";
      case "pro":        return "bg-teal-50 text-teal-700 border-teal-200";
      case "basic":      return "bg-blue-50 text-blue-700 border-blue-200";
      case "trial":
      default:           return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  // Header-checkbox state: "all" / "some" / "none". Skips the current admin
  // (self) since selecting yourself for a bulk retrigger would be a no-op
  // wrt the tour you're actively dismissing.
  const selectableUsers = users.filter(u => u.user_id !== currentUser?.user_id);
  const selectedCount = selectableUsers.filter(u => selectedUserIds?.has(u.user_id)).length;
  const allSelected = selectableUsers.length > 0 && selectedCount === selectableUsers.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const toggleAll = () => {
    if (!onToggleSelected) return;
    if (allSelected) {
      // Clear every selectable user from the set.
      selectableUsers.forEach(u => onToggleSelected(u.user_id));
    } else {
      // Add every selectable user that isn't already selected.
      selectableUsers
        .filter(u => !selectedUserIds?.has(u.user_id))
        .forEach(u => onToggleSelected(u.user_id));
    }
  };

  if (!users.length) {
    return (
      <div className="text-center py-12 text-gray-400">
        No users to display.
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-x-auto bg-white" data-testid="user-management-table">
      <table className="w-full text-sm min-w-[1420px]">
        <thead className="bg-gray-50 border-b border-gray-200 text-left">
          <tr className="text-xs text-gray-500">
            <th className="pl-4 pr-2 py-2.5 font-medium w-8">
              <Checkbox
                checked={allSelected || (someSelected && "indeterminate")}
                onCheckedChange={toggleAll}
                aria-label="Select all users"
                data-testid="select-all-users"
              />
            </th>
            <th className="px-4 py-2.5 font-medium">User</th>
            <th className="px-4 py-2.5 font-medium">Org</th>
            <th className="px-4 py-2.5 font-medium">Plan</th>
            <th className="px-4 py-2.5 font-medium text-right">Active</th>
            <th className="px-4 py-2.5 font-medium text-right">Purchased</th>
            <th className="px-4 py-2.5 font-medium">Templates</th>
            <th className="px-4 py-2.5 font-medium text-right">Reports</th>
            <th className="px-4 py-2.5 font-medium text-right">Slides</th>
            <th className="px-4 py-2.5 font-medium text-right">Decks</th>
            <th className="px-4 py-2.5 font-medium text-right">Deck slides</th>
            <th className="px-4 py-2.5 font-medium text-right">Queued</th>
            <th className="px-4 py-2.5 font-medium">Joined</th>
            <th className="px-4 py-2.5 font-medium">Last login</th>
            <th className="px-4 py-2.5 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {users.map(u => {
            const stats = u.stats || {};
            const at = u.account_type || "trial";
            const org = u.organization || u.organization_inferred || "—";
            const isSelf = u.user_id === currentUser?.user_id;
            const tmplNames = getTemplateNames(u.template_access);
            return (
              <tr key={u.user_id} className="hover:bg-gray-50" data-testid={`user-row-${u.user_id}`}>
                <td className="pl-4 pr-2 py-3 align-top">
                  <Checkbox
                    checked={selectedUserIds?.has(u.user_id) || false}
                    onCheckedChange={() => onToggleSelected && onToggleSelected(u.user_id)}
                    disabled={isSelf}
                    aria-label={`Select ${u.email}`}
                    data-testid={`select-user-${u.user_id}`}
                  />
                </td>
                <td className="px-4 py-3 align-top">
                  <div className="flex items-center gap-2.5 min-w-[220px]">
                    {u.picture ? (
                      <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-medium text-gray-900 truncate max-w-[180px]" title={u.name}>{u.name || "No name"}</span>
                        {u.role === "admin" && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0">
                            <Shield className="w-2.5 h-2.5" />Admin
                          </span>
                        )}
                        {isSelf && <span className="text-[9px] font-medium text-gray-500 bg-gray-100 rounded-full px-1.5 py-0">You</span>}
                      </div>
                      <Link
                        to={`/admin/users/${u.user_id}/credits`}
                        className="block text-xs text-gray-400 hover:text-teal-700 hover:underline truncate max-w-[220px]"
                        title={`${u.email} — view credits ledger`}
                        data-testid={`email-link-${u.user_id}`}
                      >
                        {u.email}
                      </Link>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-700 align-top">
                  <span className="inline-flex items-center gap-1 text-xs">
                    <Building className="w-3 h-3 text-gray-400" />{org}
                  </span>
                </td>
                <td className="px-4 py-3 align-top">
                  <span className={`inline-flex text-[11px] font-semibold rounded-full px-2 py-0.5 border ${planBadgeClass(at)}`} data-testid={`plan-badge-${u.user_id}`}>
                    {at.charAt(0).toUpperCase() + at.slice(1)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right align-top">
                  <span className="inline-flex items-center gap-1 text-xs text-gray-700 font-medium">
                    <Coins className="w-3 h-3 text-amber-500" />{u.credits_balance ?? 0}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-xs text-gray-700 align-top" data-testid={`purchased-${u.user_id}`}>
                  {u.credits_purchased_total ?? 0}
                </td>
                <td className="px-4 py-3 align-top">
                  <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                    <Palette className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    <span className="truncate max-w-[180px]" title={tmplNames}>{tmplNames}</span>
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-xs text-gray-700 align-top">
                  <span className="inline-flex items-center gap-1"><BookOpen className="w-3 h-3 text-gray-400" />{stats.reports_total ?? 0}</span>
                </td>
                <td className="px-4 py-3 text-right text-xs text-gray-700 align-top">
                  <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3 text-gray-400" />{stats.singles_complete ?? 0}</span>
                </td>
                <td className="px-4 py-3 text-right text-xs text-gray-700 align-top">
                  <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3 text-gray-400" />{stats.decks_complete ?? 0}</span>
                </td>
                <td className="px-4 py-3 text-right text-xs text-gray-700 align-top">{stats.deck_slides_complete ?? 0}</td>
                <td className="px-4 py-3 text-right align-top">
                  {(stats.queued_total ?? 0) > 0 ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                      <Clock className="w-3 h-3" />{stats.queued_total}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500 align-top whitespace-nowrap">
                  {u.created_at ? fmtDate(u.created_at) : "—"}
                </td>
                <td
                  className="px-4 py-3 text-xs align-top whitespace-nowrap"
                  data-testid={`last-login-${u.user_id}`}
                  title={u.last_login_at ? `${u.last_login_at}${u.login_count ? ` · ${u.login_count} logins` : ""}` : "Never logged in"}
                >
                  {u.last_login_at ? (
                    <span className="text-gray-700">{fmtRelative(u.last_login_at)}</span>
                  ) : (
                    <span className="text-gray-300">Never</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right align-top whitespace-nowrap">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => onLogs(u)}
                      className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                      title="View this user's generation logs"
                      data-testid={`logs-${u.user_id}`}
                    >
                      <BookOpen className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onLedger(u)}
                      className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                      title="View credits ledger"
                      data-testid={`ledger-${u.user_id}`}
                    >
                      <Coins className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onImpersonate(u)}
                      disabled={isSelf || u.role === "admin" || impersonatingId === u.user_id}
                      className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title={u.role === "admin" ? "Cannot impersonate another admin" : "Login as this user"}
                      data-testid={`row-impersonate-${u.user_id}`}
                    >
                      {impersonatingId === u.user_id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <UserCog className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => onEdit(u)}
                      className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                      title="Edit user"
                      data-testid={`row-edit-${u.user_id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onRetriggerOnboarding && onRetriggerOnboarding(u)}
                      disabled={retriggeringUserId === u.user_id}
                      className="p-1.5 text-teal-500 hover:text-teal-700 hover:bg-teal-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Re-trigger the onboarding coachmark tour"
                      data-testid={`row-retrigger-onboarding-${u.user_id}`}
                    >
                      {retriggeringUserId === u.user_id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Sparkles className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => onDelete(u)}
                      disabled={isSelf}
                      className="p-1.5 text-red-400 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Delete user"
                      data-testid={`row-delete-${u.user_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

